import {
  COST_KINDS,
  ESTIMATE_ITEM_TYPES,
  ESTIMATE_VERSION_KINDS,
  type EstimateCard,
  type EstimateCheckFinding,
  type EstimateImportResult,
  type EstimateItemResourceRow,
  type EstimateItemRow,
  type EstimateStatus,
  RESOURCE_KINDS,
  type RepriceReport,
  type ResourceStatement,
  SSR_CHAPTERS,
  seesMoney,
} from '@epc/contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, Download, FileUp, Lock, Plus, Trash2, Unlock } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, tenge, tengeShort } from '../../lib/format';
import { budgetQuery, projectQuery } from '../../lib/projects';
import { useCan, useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { ChangeImpact } from './ChangeImpact';
import {
  AddItemDialog,
  CoefficientsDialog,
  Kenml2Dialog,
  ReplaceResourceDialog,
  RepriceDialog,
  RepriceReportDialog,
} from './EditDialogs';
import { ImportDialog, ImportResultDialog, StatusBadge } from './EstimatesPage';
import { ObjectFormTab, SsrTab } from './Forms';
import { ItemComments } from './ItemComments';
import { ItemLinks } from './ItemLinks';
import { ItemScheduleLinks } from './ItemScheduleLinks';
import { ItemTakeoff } from './ItemTakeoff';
import { estimateDiffQuery, estimateQuery, estimateResourcesQuery, hours, qty, sum } from './lib';
import { OfferCompareTab } from './OfferCompare';

const TYPE_TONE: Record<EstimateItemRow['type'], 'neutral' | 'info' | 'accent' | 'warning' | 'success'> = {
  work: 'neutral',
  material: 'info',
  equipment: 'accent',
  price: 'warning',
  other: 'success',
};

function ItemResources({
  estimateId,
  item,
  projectId,
  money,
  editable,
  canLinkSchedule,
  onChanged,
}: {
  estimateId: string;
  item: EstimateItemRow;
  projectId: string;
  money: boolean;
  editable: boolean;
  canLinkSchedule: boolean;
  onChanged: () => void;
}) {
  const [replace, setReplace] = useState<EstimateItemResourceRow | null>(null);
  const [coefs, setCoefs] = useState(false);
  const q = useQuery({
    queryKey: ['estimate', estimateId, 'item', item.id, 'resources'],
    queryFn: () => api<EstimateItemResourceRow[]>(`/estimates/${estimateId}/items/${item.id}/resources`),
  });
  if (q.isPending) return <Spinner label="Ресурсы" />;
  if (q.isError) return <ErrorBox error={q.error} />;
  return (
    <div className="flex flex-col gap-3 py-2">
      {item.coefficients.length ? (
        <ul className="flex flex-col gap-1 text-[12px] text-ink-2">
          {item.coefficients.map((c) => (
            <li key={`${c.code ?? ''}|${c.name ?? ''}|${c.kLabor}|${c.kMachines}|${c.kMaterials}`}>
              <span className="font-medium text-ink">{c.code ?? 'Коэффициент'}</span>
              {c.kLabor !== null ? ` · труд ${c.kLabor}` : ''}
              {c.kMachines !== null ? ` · машины ${c.kMachines}` : ''}
              {c.kMaterials !== null ? ` · материалы ${c.kMaterials}` : ''}
              {c.name ? ` — ${c.name}` : ''}
              {c.basis ? ` (${c.basis})` : ''}
            </li>
          ))}
        </ul>
      ) : null}
      {item.issues.length ? (
        <ul className="list-disc pl-5 text-[12px] text-warning">
          {item.issues.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      ) : null}
      {q.data.length ? (
        // На телефоне таблица ресурсов прокручивается сама, страница вбок не уезжает
        <div className="max-w-full overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-ink-2">
                <th className="py-1 pr-2 font-medium">Код</th>
                <th className="py-1 pr-2 font-medium">Ресурс</th>
                <th className="py-1 pr-2 font-medium">Ед.</th>
                <th className="py-1 pr-2 text-right font-medium">Норма</th>
                <th className="py-1 pr-2 text-right font-medium">Объём</th>
                <th className="py-1 pr-2 text-right font-medium">Цена</th>
                <th className="py-1 text-right font-medium">Сумма</th>
                {editable ? <th className="py-1" /> : null}
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => (
                <tr key={r.id} className="border-t border-line/60">
                  <td className="num py-1 pr-2 whitespace-nowrap text-ink-2">{r.code}</td>
                  <td className="py-1 pr-2 [overflow-wrap:anywhere]">
                    <span className="mr-1 text-ink-2">
                      {RESOURCE_KINDS[r.kind as keyof typeof RESOURCE_KINDS] ?? r.kind}:
                    </span>
                    {r.name}
                    {r.replacedCode ? (
                      <span className="block text-ink-2">
                        замена {r.replacedCode}
                        {r.replaceReason ? `: ${r.replaceReason}` : ''}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-1 pr-2 whitespace-nowrap">{r.unit}</td>
                  <td className="num py-1 pr-2 text-right">{qty(r.norm)}</td>
                  <td className="num py-1 pr-2 text-right">{qty(r.quantity)}</td>
                  <td className="num py-1 pr-2 text-right">{money ? sum(r.price) : '—'}</td>
                  <td className="num py-1 text-right">{money ? sum(r.amount) : '—'}</td>
                  {editable ? (
                    <td className="py-1 pl-2 text-right">
                      <button
                        type="button"
                        className="inline-flex min-h-[44px] items-center text-[12px] text-accent-text underline md:min-h-[28px]"
                        onClick={() => setReplace(r)}
                      >
                        заменить
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[12px] text-ink-2">У позиции нет ресурсов: цена задана за единицу.</p>
      )}
      {editable && item.type === 'work' ? (
        <div>
          <button
            type="button"
            className="inline-flex min-h-[44px] items-center text-[12px] text-accent-text underline md:min-h-[28px]"
            onClick={() => setCoefs(true)}
          >
            Коэффициенты позиции
          </button>
        </div>
      ) : null}
      {replace ? (
        <ReplaceResourceDialog
          estimateId={estimateId}
          item={item}
          resource={replace}
          onClose={() => setReplace(null)}
          onSaved={() => {
            setReplace(null);
            q.refetch();
            onChanged();
          }}
        />
      ) : null}
      {coefs ? (
        <CoefficientsDialog
          estimateId={estimateId}
          item={item}
          onClose={() => setCoefs(false)}
          onSaved={() => {
            setCoefs(false);
            q.refetch();
            onChanged();
          }}
        />
      ) : null}
      <ItemScheduleLinks
        estimateId={estimateId}
        itemId={item.id}
        projectId={projectId}
        canEdit={canLinkSchedule}
      />
    </div>
  );
}

type SectionRow = EstimateCard['sections'][number];

/** Сколько позиций грузится за раз: смета на 20 000 позиций открывается первой страницей (est-10) */
const ITEMS_PAGE = 200;

/** Часть списка позиций: раздел, позиции вне разделов, вся смета (один раздел) или найденное */
interface ItemsGroup {
  id: string;
  section: SectionRow | null;
  /** Фильтр страницы: id раздела, none — вне разделов, пусто — вся смета */
  sectionId?: string;
  count: number;
  heading: boolean;
}

/** Группы списка: при одном разделе — вся смета без заголовка, иначе — разделы и «вне разделов» в конце */
function groupsOf(card: EstimateCard): ItemsGroup[] {
  if (card.sections.length <= 1)
    return [{ id: 'all', section: null, count: card.itemsCount, heading: false }];
  const groups: ItemsGroup[] = card.sections.map((s) => ({
    id: s.id,
    section: s,
    sectionId: s.id,
    count: s.itemsCount,
    heading: true,
  }));
  const loose = card.itemsCount - card.sections.reduce((n, s) => n + s.itemsCount, 0);
  if (loose > 0) groups.push({ id: 'none', section: null, sectionId: 'none', count: loose, heading: true });
  return groups;
}

/** Позиции страницами по курсору; раздел грузится, когда его раскрыли (est-10) */
function useItemPages(estimateId: string, sectionId: string | undefined, q: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['estimate', estimateId, 'items', sectionId ?? 'all', q],
    queryFn: ({ pageParam }) =>
      api<EstimateCard>(`/estimates/${estimateId}`, {
        query: { limit: ITEMS_PAGE, sectionId, q: q || undefined, cursor: pageParam ?? undefined },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.itemsPage?.nextCursor ?? null,
    enabled,
  });
}

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function GroupToggle({
  group,
  expanded,
  onToggle,
}: {
  group: ItemsGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="flex min-h-[44px] w-full min-w-0 items-center gap-1.5 text-left text-[13px] font-medium text-ink md:min-h-[28px] md:w-auto md:flex-1"
      onClick={onToggle}
      aria-expanded={expanded}
    >
      {expanded ? (
        <ChevronDown className="size-4 shrink-0 text-ink-2" />
      ) : (
        <ChevronRight className="size-4 shrink-0 text-ink-2" />
      )}
      <span className="min-w-0 [overflow-wrap:anywhere]">
        {group.section?.number ? <span className="num mr-1.5 text-ink-2">{group.section.number}</span> : null}
        {group.section ? group.section.name : 'Вне разделов'}
        <span className="num ml-1.5 text-[12px] font-normal text-ink-2">· {group.count} поз.</span>
      </span>
    </button>
  );
}

function SectionHeading({
  group,
  expanded,
  onToggle,
  money,
  card,
  onLock,
  locking,
}: {
  group: ItemsGroup;
  expanded: boolean;
  onToggle: () => void;
  money: boolean;
  card: EstimateCard;
  onLock: (s: SectionRow) => void;
  locking: boolean;
}) {
  const section = group.section;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <GroupToggle group={group} expanded={expanded} onToggle={onToggle} />
      {section?.lockedBy ? <Badge tone="info">заблокирован: {section.lockedByName ?? ''}</Badge> : null}
      {section ? (
        <span className="num text-[13px] text-ink-2">{money ? sum(section.totals.amount) : ''}</span>
      ) : null}
      {section && card.canEdit ? (
        <button
          type="button"
          className="flex min-h-[44px] items-center gap-1 text-[12px] text-ink-2 hover:text-ink md:min-h-0"
          disabled={locking}
          onClick={() => onLock(section)}
          aria-label={section.lockedBy ? 'Снять блокировку раздела' : 'Заблокировать раздел за собой'}
        >
          {section.lockedBy ? <Unlock className="size-4" /> : <Lock className="size-4" />}
          {section.lockedBy ? 'снять блокировку' : 'заблокировать'}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Строки одной группы: заголовок раздела, загруженные страницы позиций и «Показать ещё». Одна и та же
 * группа выводится списком на телефоне и строками таблицы на компьютере — запрос у них общий (кэш).
 */
function GroupRows({
  estimateId,
  group,
  q,
  expanded,
  mode,
  heading,
  renderItem,
  split,
}: {
  estimateId: string;
  group: ItemsGroup;
  q: string;
  expanded: boolean;
  mode: 'mobile' | 'desktop';
  heading: ReactNode;
  renderItem: (it: EstimateItemRow) => ReactNode;
  /** Небольшая смета: все позиции одним запросом, заголовки разделов — по загруженным позициям */
  split?: {
    groups: ItemsGroup[];
    isOpen: (g: ItemsGroup, i: number) => boolean;
    heading: (g: ItemsGroup, i: number) => ReactNode;
  };
}) {
  const pages = useItemPages(estimateId, group.sectionId, q, expanded);
  const items = pages.data?.pages.flatMap((p) => p.items) ?? [];
  const total = pages.data?.pages[0]?.itemsPage?.total ?? group.count;
  const left = Math.max(0, total - items.length);
  const note = !expanded ? null : pages.isPending ? (
    <Spinner label="Загрузка позиций" />
  ) : pages.isError ? (
    <ErrorBox error={pages.error} />
  ) : (
    <div className="flex flex-wrap items-center gap-3 text-[13px] text-ink-2">
      {q ? <span>{total ? `Найдено: ${total}` : 'Ничего не найдено'}</span> : null}
      {pages.hasNextPage ? (
        <Button
          className="min-h-[44px] md:min-h-0"
          size="sm"
          variant="ghost"
          loading={pages.isFetchingNextPage}
          onClick={() => pages.fetchNextPage()}
        >
          Показать ещё {Math.min(ITEMS_PAGE, left)} из {left}
        </Button>
      ) : null}
    </div>
  );
  const showNote = expanded && (pages.isPending || pages.isError || q !== '' || pages.hasNextPage);
  // Части: заголовок и позиции; без split — одна часть
  const known = new Set(split?.groups.map((g) => g.sectionId));
  const parts: Array<{ key: string; heading: ReactNode; items: EstimateItemRow[] }> = split
    ? split.groups.map((g, i) => ({
        key: g.id,
        heading: split.heading(g, i),
        items: split.isOpen(g, i)
          ? items.filter((it) =>
              g.sectionId === 'none'
                ? !it.sectionId || !known.has(it.sectionId)
                : it.sectionId === g.sectionId,
            )
          : [],
      }))
    : [{ key: group.id, heading, items: expanded ? items : [] }];
  if (mode === 'mobile')
    return (
      <>
        {parts.map((pt) => (
          <Fragment key={pt.key}>
            {pt.heading ? <li className="bg-surface-2 px-4 py-2">{pt.heading}</li> : null}
            {pt.items.map(renderItem)}
          </Fragment>
        ))}
        {showNote ? <li className="px-4 py-2">{note}</li> : null}
      </>
    );
  return (
    <>
      {parts.map((pt) => (
        <Fragment key={pt.key}>
          {pt.heading ? (
            <tr className="border-t border-line bg-surface-2/70">
              <td colSpan={10} className="px-5 py-2">
                {pt.heading}
              </td>
            </tr>
          ) : null}
          {pt.items.map(renderItem)}
        </Fragment>
      ))}
      {showNote ? (
        <tr className="border-t border-line">
          <td colSpan={10} className="px-5 py-2">
            {note}
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ItemsTable({
  card,
  money,
  editable,
  canLinkSchedule,
  onChanged,
  onAddAfter,
  onDelete,
  onLock,
  locking,
}: {
  card: EstimateCard;
  money: boolean;
  editable: boolean;
  canLinkSchedule: boolean;
  onChanged: () => void;
  onAddAfter: (id: string) => void;
  onDelete: (it: EstimateItemRow) => void;
  onLock: (s: SectionRow) => void;
  locking: boolean;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const unitPrice = (it: EstimateItemRow) =>
    it.type === 'equipment' ? it.unit_.equipment : it.type === 'price' ? it.unit_.amount : it.unit_.direct;
  // Разделы раскрываются и подгружаются по одному; небольшая смета открыта целиком, большая — первым разделом
  const [search, setSearch] = useState('');
  const term = useDebounced(search.trim());
  // Номер позиции ищется с одной цифры, код и слова — с двух знаков
  const q = term.length >= 2 || /^\d+$/.test(term) ? term : '';
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const groups: ItemsGroup[] = q
    ? [{ id: 'search', section: null, count: 0, heading: false }]
    : groupsOf(card);
  // Небольшая смета (до страницы) грузится одним запросом и открыта целиком; большая — разделами, первый открыт
  const small = !q && card.itemsCount <= ITEMS_PAGE;
  const isOpen = (g: ItemsGroup, i: number) =>
    q !== '' || !g.heading || (openGroups[g.id] ?? (small || i === 0));
  const toggleGroup = (g: ItemsGroup, i: number) => setOpenGroups((o) => ({ ...o, [g.id]: !isOpen(g, i) }));
  const heading = (g: ItemsGroup, i: number) =>
    g.heading ? (
      <SectionHeading
        group={g}
        expanded={isOpen(g, i)}
        onToggle={() => toggleGroup(g, i)}
        money={money}
        card={card}
        onLock={onLock}
        locking={locking}
      />
    ) : null;
  const mobileItem = (it: EstimateItemRow) => (
    <li key={it.id} className="px-4 py-3">
      <button
        type="button"
        className="flex min-h-[44px] w-full items-start gap-3 text-left"
        onClick={() => toggle(it.id)}
        aria-expanded={Boolean(open[it.id])}
      >
        <span className="num mt-0.5 w-7 shrink-0 text-[13px] text-ink-2">{it.number}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-[14px] text-ink [overflow-wrap:anywhere]">{it.name}</span>
          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-ink-2">
            {(it.code ?? it.localCode) ? <span className="num">{it.code ?? it.localCode}</span> : null}
            <Badge tone={TYPE_TONE[it.type]}>{ESTIMATE_ITEM_TYPES[it.type]}</Badge>
            <span>
              {qty(it.quantity)} {it.unit}
            </span>
            {it.issues.length ? <Badge tone="warning">расхождение</Badge> : null}
          </span>
          <span className="num mt-1 block text-[14px] font-medium text-ink">
            {money ? tenge(it.total.amount) : 'суммы скрыты'}
          </span>
        </span>
        {open[it.id] ? (
          <ChevronDown className="mt-1 size-4 shrink-0 text-ink-2" />
        ) : (
          <ChevronRight className="mt-1 size-4 shrink-0 text-ink-2" />
        )}
      </button>
      {open[it.id] ? (
        <>
          <ItemResources
            estimateId={card.id}
            item={it}
            projectId={card.projectId}
            money={money}
            editable={editable}
            canLinkSchedule={canLinkSchedule}
            onChanged={onChanged}
          />
          <ItemLinks projectId={card.projectId} itemId={it.id} editable={editable} />
          <ItemTakeoff
            estimateId={card.id}
            projectId={card.projectId}
            item={it}
            editable={editable}
            money={money}
            onChanged={onChanged}
          />
          <ItemComments itemId={it.id} />
          {editable ? (
            <div className="flex flex-wrap gap-2 pb-1">
              <Button
                className="min-h-[44px] md:min-h-0"
                size="sm"
                variant="ghost"
                icon={<Plus className="size-4" />}
                onClick={() => onAddAfter(it.id)}
              >
                Позиция после
              </Button>
              <Button
                className="min-h-[44px] md:min-h-0"
                size="sm"
                variant="ghost"
                icon={<Trash2 className="size-4" />}
                onClick={() => onDelete(it)}
              >
                Удалить позицию
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </li>
  );
  const desktopItem = (it: EstimateItemRow) => (
    <Fragment key={it.id}>
      <tr className={clsx('border-t border-line', open[it.id] && 'bg-surface-2/60')}>
        <td className="py-2 pr-2 pl-5 align-top">
          <button
            type="button"
            className="num flex min-h-[28px] items-center gap-1 text-ink-2 hover:text-ink"
            onClick={() => toggle(it.id)}
            aria-expanded={Boolean(open[it.id])}
            aria-label={`Позиция ${it.number}: ресурсы`}
          >
            {open[it.id] ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
            {it.number}
          </button>
        </td>
        <td className="py-2 pr-2 align-top">
          <span className="num block text-ink [overflow-wrap:anywhere]">
            {it.code ?? it.localCode ?? '—'}
          </span>
          <span className="block text-[12px] text-ink-2 [overflow-wrap:anywhere]">{it.source}</span>
        </td>
        <td className="py-2 pr-2 align-top [overflow-wrap:anywhere]">
          <span className="block text-ink">{it.name}</span>
          <span className="mt-1 flex flex-wrap items-center gap-1.5">
            <Badge tone={TYPE_TONE[it.type]}>{ESTIMATE_ITEM_TYPES[it.type]}</Badge>
            <Badge>{COST_KINDS[it.costKind as keyof typeof COST_KINDS] ?? it.costKind}</Badge>
            {it.coefficients.length ? <Badge tone="info">К × {it.coefficients.length}</Badge> : null}
            {it.priceIndex !== null ? <Badge tone="warning">К приведения {it.priceIndex}</Badge> : null}
            {it.issues.length ? <Badge tone="warning">расхождение</Badge> : null}
            {it.noVolumeSource ? <Badge tone="danger">без объёма по ведомости</Badge> : null}
            {it.takeoffId ? <Badge tone="success">объём по ведомости</Badge> : null}
            {it.analog ? <Badge>аналог</Badge> : null}
          </span>
        </td>
        <td className="py-2 pr-2 align-top whitespace-nowrap">{it.unit}</td>
        <td className="num py-2 pr-2 text-right align-top">{qty(it.quantity)}</td>
        <td className="num py-2 pr-2 text-right align-top">{money ? sum(unitPrice(it)) : '—'}</td>
        <td className="num py-2 pr-2 text-right align-top text-ink-2">{money ? sum(it.total.wages) : '—'}</td>
        <td className="num py-2 pr-2 text-right align-top text-ink-2">
          {money ? sum(it.total.machines) : '—'}
        </td>
        <td className="num py-2 pr-2 text-right align-top text-ink-2">
          {money ? sum(it.total.materials) : '—'}
        </td>
        <td className="num py-2 pr-5 text-right align-top font-medium">
          {money ? sum(it.total.amount) : '—'}
        </td>
      </tr>
      {open[it.id] ? (
        <tr className="border-t border-line/60 bg-surface-2/40">
          <td />
          <td colSpan={9} className="px-2 pr-5">
            <ItemResources
              estimateId={card.id}
              item={it}
              projectId={card.projectId}
              money={money}
              editable={editable}
              canLinkSchedule={canLinkSchedule}
              onChanged={onChanged}
            />
            <ItemLinks projectId={card.projectId} itemId={it.id} editable={editable} />
            <ItemTakeoff
              estimateId={card.id}
              projectId={card.projectId}
              item={it}
              editable={editable}
              money={money}
              onChanged={onChanged}
            />
            <ItemComments itemId={it.id} />
            {editable ? (
              <div className="flex flex-wrap gap-2 pb-2">
                <Button
                  className="min-h-[44px] md:min-h-0"
                  size="sm"
                  variant="ghost"
                  icon={<Plus className="size-4" />}
                  onClick={() => onAddAfter(it.id)}
                >
                  Позиция после
                </Button>
                <Button
                  className="min-h-[44px] md:min-h-0"
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="size-4" />}
                  onClick={() => onDelete(it)}
                >
                  Удалить позицию
                </Button>
              </div>
            ) : null}
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
  const rowsFor = (mode: 'mobile' | 'desktop', renderItem: (it: EstimateItemRow) => ReactNode) =>
    small && groups.length > 1 ? (
      <GroupRows
        estimateId={card.id}
        group={{ id: 'all', section: null, count: card.itemsCount, heading: false }}
        q=""
        expanded
        mode={mode}
        heading={null}
        renderItem={renderItem}
        split={{ groups, isOpen, heading }}
      />
    ) : (
      groups.map((g, i) => (
        <GroupRows
          key={`${g.id}:${q}`}
          estimateId={card.id}
          group={g}
          q={q}
          expanded={isOpen(g, i)}
          mode={mode}
          heading={heading(g, i)}
          renderItem={renderItem}
        />
      ))
    );
  const searchBox = (
    <div className="border-b border-line px-4 py-3 md:px-5">
      <Input
        type="search"
        className="min-h-[44px] w-full md:max-w-md"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Поиск: код, номер или слова наименования"
        aria-label="Поиск позиций сметы по коду, номеру и словам наименования"
      />
    </div>
  );
  return (
    <>
      {searchBox}
      {/* Телефон: карточки позиций без прокрутки вбок */}
      <ul className="divide-y divide-line md:hidden">{rowsFor('mobile', mobileItem)}</ul>
      <div className="hidden md:block">
        <ScrollX stickyFirst={false}>
          <table className="w-full min-w-[960px] text-[13px]">
            <thead>
              <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                <th className="py-2.5 pr-2 pl-5 font-medium">№</th>
                <th className="py-2.5 pr-2 font-medium">Код и обоснование</th>
                <th className="py-2.5 pr-2 font-medium">Наименование</th>
                <th className="py-2.5 pr-2 font-medium">Ед.</th>
                <th className="py-2.5 pr-2 text-right font-medium">Кол-во</th>
                <th className="py-2.5 pr-2 text-right font-medium">Цена ед.</th>
                <th className="py-2.5 pr-2 text-right font-medium">ЗП</th>
                <th className="py-2.5 pr-2 text-right font-medium">ЭМ</th>
                <th className="py-2.5 pr-2 text-right font-medium">МТ</th>
                <th className="py-2.5 pr-5 text-right font-medium">Всего</th>
              </tr>
            </thead>
            <tbody>{rowsFor('desktop', desktopItem)}</tbody>
          </table>
        </ScrollX>
      </div>
    </>
  );
}

export function StatementTable({
  st,
  money,
  exportHref,
}: {
  st: ResourceStatement;
  money: boolean;
  /** Ссылка на выгрузку ведомости в Excel (СМ-10) */
  exportHref?: string;
}) {
  const groups = ['labor', 'machinist', 'machine', 'material', 'equipment'] as const;
  return (
    <div className="flex flex-col gap-4">
      {exportHref ? (
        <div>
          <a
            href={exportHref}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-line px-4 text-[14px] font-medium text-ink hover:bg-surface-2 md:min-h-10"
          >
            <Download className="size-4" /> Ведомость в Excel
          </a>
        </div>
      ) : null}
      <TitleBlock
        items={[
          { label: 'Трудоёмкость', value: hours(st.totals.laborHours), note: 'чел.-ч рабочих и машинистов' },
          { label: 'Машины', value: hours(st.totals.machineHours), note: 'маш.-ч' },
          {
            label: 'ФОТ',
            value: money ? tengeShort(st.totals.wages) : '—',
            note: money ? tenge(st.totals.wages) : 'суммы скрыты',
          },
          {
            label: 'Материалы',
            value: money ? tengeShort(st.totals.materials) : '—',
            note: money ? tenge(st.totals.materials) : 'суммы скрыты',
          },
          {
            label: 'Оборудование',
            value: money ? tengeShort(st.totals.equipment) : '—',
            note: money ? tenge(st.totals.equipment) : 'суммы скрыты',
          },
        ]}
      />
      {groups.map((g) => {
        const rows = st.rows.filter((r) => r.kind === g);
        if (!rows.length) return null;
        return (
          <Panel key={g} title={RESOURCE_KINDS[g]} meta={`${rows.length} строк`} bodyClassName="p-0">
            <ul className="divide-y divide-line md:hidden">
              {rows.map((r) => (
                <li key={`${r.code}-${r.unit}`} className="px-4 py-3 text-[13px]">
                  <span className="num text-ink-2">{r.code}</span>
                  <span className="block text-ink [overflow-wrap:anywhere]">{r.name}</span>
                  <span className="num block text-ink-2">
                    {qty(r.quantity)} {r.unit}
                    {money && r.amount !== null ? ` · ${tenge(r.amount)}` : ''}
                  </span>
                </li>
              ))}
            </ul>
            <div className="hidden md:block">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                    <th className="py-2.5 pr-2 pl-5 font-medium">Код</th>
                    <th className="py-2.5 pr-2 font-medium">Наименование</th>
                    <th className="py-2.5 pr-2 font-medium">Ед.</th>
                    <th className="py-2.5 pr-2 text-right font-medium">Объём</th>
                    <th className="py-2.5 pr-2 text-right font-medium">Цена</th>
                    <th className="py-2.5 pr-2 text-right font-medium">Сумма</th>
                    <th className="py-2.5 pr-5 text-right font-medium">Позиций</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.code}-${r.unit}`} className="border-t border-line">
                      <td className="num py-2 pr-2 pl-5 whitespace-nowrap text-ink-2">{r.code}</td>
                      <td className="py-2 pr-2 [overflow-wrap:anywhere]">{r.name}</td>
                      <td className="py-2 pr-2 whitespace-nowrap">{r.unit}</td>
                      <td className="num py-2 pr-2 text-right">{qty(r.quantity)}</td>
                      <td className="num py-2 pr-2 text-right">{money ? sum(r.price) : '—'}</td>
                      <td className="num py-2 pr-2 text-right">{money ? sum(r.amount) : '—'}</td>
                      <td className="num py-2 pr-5 text-right text-ink-2">{r.items}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        );
      })}
    </div>
  );
}

function DiffPanel({ card, otherId, money }: { card: EstimateCard; otherId: string; money: boolean }) {
  const q = useQuery(estimateDiffQuery(otherId, card.id));
  if (q.isPending) return <Spinner label="Сравнение" />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data;
  const label = (c: string) =>
    c === 'added' ? 'добавлена' : c === 'removed' ? 'убрана' : c === 'changed' ? 'изменена' : '';
  const tone = (c: string) =>
    c === 'added' ? 'success' : c === 'removed' ? 'danger' : c === 'changed' ? 'warning' : 'neutral';
  const changed = d.rows.filter((r) => r.change !== 'same');
  return (
    <Panel
      title={`Сравнение с версией ${d.a.versionNo} (${ESTIMATE_VERSION_KINDS[d.a.versionKind as keyof typeof ESTIMATE_VERSION_KINDS] ?? d.a.versionKind})`}
      meta={`добавлено ${d.totals.added}, убрано ${d.totals.removed}, изменено ${d.totals.changed}${money ? `, разница ${tenge(d.totals.delta)}` : ''}`}
      bodyClassName="p-0"
    >
      <div className="grid grid-cols-2 gap-px border-b border-line bg-line text-[13px] sm:grid-cols-4">
        {d.bySection.map((s) => (
          <div key={s.section} className="bg-surface px-4 py-3">
            <span className="block text-[12px] text-ink-2">{s.section}</span>
            <span className="num block">{money ? sum(s.amountB) : '—'}</span>
            <span
              className={clsx(
                'num block text-[12px]',
                (s.delta ?? 0) > 0 ? 'text-danger' : (s.delta ?? 0) < 0 ? 'text-success' : 'text-ink-2',
              )}
            >
              {money && s.delta ? `${s.delta > 0 ? '+' : ''}${sum(s.delta)}` : money ? 'без изменений' : '—'}
            </span>
          </div>
        ))}
      </div>
      {d.byEstimateSection.some((g) => g.delta || g.itemsA !== g.itemsB) ? (
        <div className="border-b border-line px-4 py-3 text-[13px]">
          <span className="mb-1 block font-medium">Итоги разделов сметы</span>
          <ul className="flex flex-col gap-1">
            {d.byEstimateSection.map((g) => (
              <li
                key={`${g.number ?? ''}|${g.name}`}
                className="flex flex-wrap gap-x-2 [overflow-wrap:anywhere]"
              >
                <span className="min-w-0 basis-full sm:basis-auto sm:flex-1">
                  {g.number ? `Раздел ${g.number}. ` : ''}
                  {g.name}
                  <span className="text-ink-2">
                    {' '}
                    · позиций {g.itemsA} → {g.itemsB}
                  </span>
                </span>
                {money ? (
                  <span className="num">
                    {sum(g.amountA)} → {sum(g.amountB)}{' '}
                    <span
                      className={clsx(
                        (g.delta ?? 0) > 0
                          ? 'text-danger'
                          : (g.delta ?? 0) < 0
                            ? 'text-success'
                            : 'text-ink-2',
                      )}
                    >
                      ({g.delta ? `${g.delta > 0 ? '+' : ''}${sum(g.delta)}` : 'без изменений'})
                    </span>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {changed.length ? (
        <ul className="divide-y divide-line">
          {changed.map((r) => (
            <li
              key={r.key}
              className="flex flex-col gap-1 px-5 py-3 text-[13px] md:flex-row md:items-center md:gap-4"
            >
              <Badge tone={tone(r.change)}>{label(r.change)}</Badge>
              <span className="num w-[130px] shrink-0 text-ink-2">{r.code ?? '—'}</span>
              <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{r.name}</span>
              <span className="num text-ink-2">
                {qty(r.quantityA)} → {qty(r.quantityB)} {r.unit}
              </span>
              <span className="num w-[150px] text-right">
                {money ? `${(r.delta ?? 0) > 0 ? '+' : ''}${sum(r.delta)}` : '—'}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-5 py-4 text-[13px] text-ink-2">
          Позиции обеих версий совпадают по объёмам и суммам.
        </p>
      )}
    </Panel>
  );
}

/** Глава сводного сметного расчёта (1–7) для объектной сметы (п. 8.2.56 НДЦС РК 8.01-08) */
function ChapterPicker({ card, onSaved }: { card: EstimateCard; onSaved: () => void }) {
  const m = useMutation({
    mutationFn: (chapter: string | null) =>
      api(`/estimates/${card.id}`, { method: 'PATCH', body: { chapter } }),
    onSuccess: () => {
      toast('Глава ССР сохранена');
      onSaved();
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <Field
      label="Глава сводного сметного расчёта"
      hint="Главы 1–7 части II по п. 8.2.56 НДЦС РК 8.01-08-2022"
    >
      <Select
        className="min-h-[44px] md:min-h-0"
        value={card.chapter ?? ''}
        disabled={!card.canEdit || m.isPending}
        onChange={(e) => m.mutate(e.target.value || null)}
      >
        <option value="">— не задана (глава 2) —</option>
        {Object.entries(SSR_CHAPTERS).map(([k, name]) => (
          <option key={k} value={k}>
            Глава {k}. {name}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function BudgetLinePicker({ card, onSaved }: { card: EstimateCard; onSaved: () => void }) {
  const b = useQuery(budgetQuery(card.projectId));
  const m = useMutation({
    mutationFn: (budgetLineId: string | null) =>
      api(`/estimates/${card.id}`, { method: 'PATCH', body: { budgetLineId } }),
    onSuccess: () => {
      toast('Раздел бюджета сохранён');
      onSaved();
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const lines = (b.data?.lines ?? []).filter((l) => l.kind === 'section');
  return (
    <Field label="Раздел бюджета (БМ-01)" hint="Сметная стоимость раздела складывается из привязанных смет">
      <Select
        className="min-h-[44px] md:min-h-0"
        value={card.budgetLineId ?? ''}
        disabled={!card.canEdit || m.isPending}
        onChange={(e) => m.mutate(e.target.value || null)}
      >
        <option value="">— не привязана —</option>
        {lines.map((l) => (
          <option key={l.id} value={l.id}>
            {l.code ? `${l.code}. ` : ''}
            {l.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}

export function EstimatePage() {
  const { estimateId } = useParams({ strict: false }) as { estimateId: string };
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery(estimateQuery(estimateId));
  const project = useQuery({ ...projectQuery(q.data?.projectId ?? ''), enabled: Boolean(q.data?.projectId) });
  const [view, setView] = useState<'items' | 'resources' | 'compare' | 'check' | 'offers' | 'form'>('items');
  // У объектной сметы и сводного расчёта первая вкладка — форма, а не пустые позиции
  const kindForTab = q.data?.kind;
  useEffect(() => {
    if (kindForTab === 'object' || kindForTab === 'summary') setView('form');
  }, [kindForTab]);
  const [compareWith, setCompareWith] = useState<string>('');
  const [newVersion, setNewVersion] = useState(false);
  // Копия утверждённой версии для правок по запросу на изменение (СМ-12)
  const copyVersion = useMutation({
    mutationFn: () =>
      api<{ estimateId: string }>(`/estimates/${estimateId}/copy-version`, {
        method: 'POST',
        body: { reason: 'Копия для правок' },
      }),
    onSuccess: (r) => {
      toast('Создана новая версия: правьте её, прежняя осталась как есть');
      navigate({ to: '/estimates/$estimateId', params: { estimateId: r.estimateId } });
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const [adding, setAdding] = useState<{ after: string | null } | null>(null);
  const [reprice, setReprice] = useState(false);
  const [kenml2, setKenml2] = useState(false);
  const [repriced, setRepriced] = useState<RepriceReport | null>(null);
  const [done, setDone] = useState<EstimateImportResult | null>(null);
  const money = seesMoney(me.access, 'acts_invoices');
  const card = q.data;
  const canLinkSchedule = useCan(
    'schedule.edit',
    card
      ? {
          projectId: card.projectId,
          legalEntityId: project.data?.legalEntityId,
          directionId: project.data?.directionId ?? null,
          projectPath: project.data?.parentId ? [project.data.parentId] : [],
        }
      : undefined,
  );
  useCrumbs(
    card
      ? [
          { label: card.projectCode, to: `/projects/${card.projectId}/estimates`, title: 'Проект' },
          { label: card.code, title: card.name },
        ]
      : null,
    q.error,
  );
  const statement = useQuery({ ...estimateResourcesQuery(estimateId), enabled: view === 'resources' });
  const check = useQuery({
    queryKey: ['estimate', estimateId, 'check'],
    queryFn: () => api<EstimateCheckFinding[]>(`/estimates/${estimateId}/check`),
    enabled: view === 'check',
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['estimate', estimateId] });
    qc.invalidateQueries({ queryKey: ['estimates'] });
  };
  const status = useMutation({
    mutationFn: (s: EstimateStatus) => api(`/estimates/${estimateId}/status`, { body: { status: s } }),
    onSuccess: (_, s) => {
      toast(
        s === 'approved'
          ? 'Смета утверждена'
          : s === 'review'
            ? 'Смета отправлена на проверку'
            : s === 'archived'
              ? 'Смета в архиве'
              : 'Смета возвращена в черновик',
      );
      refresh();
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const del = useMutation({
    mutationFn: (itemId: string) => api(`/estimates/${estimateId}/items/${itemId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Позиция удалена');
      refresh();
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const lock = useMutation({
    mutationFn: (p: { sectionId: string; lock: boolean }) =>
      api(`/estimates/${estimateId}/sections/${p.sectionId}/lock`, { body: { lock: p.lock } }),
    onSuccess: refresh,
    onError: (e) => toast((e as Error).message, 'danger'),
  });

  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!card) return null;
  const otherVersions = card.versions.filter((v) => v.id !== card.id);
  const editable = card.canEdit && money && card.status !== 'approved' && card.status !== 'archived';
  const title = `${card.code} — ${card.name}`;
  if (typeof document !== 'undefined') document.title = `${card.code} — EPC Pilot AI`;

  return (
    <Page>
      <PageHeader
        title={title}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={card.status} />
            <span>
              Версия {card.versionNo} · {ESTIMATE_VERSION_KINDS[card.versionKind]}
            </span>
            {!card.isCurrent ? <Badge tone="warning">прежняя версия</Badge> : null}
            {card.objectName ? <span>· {card.objectName}</span> : null}
            {card.priceLevel ? <span>· {card.priceLevel}</span> : null}
          </span>
        }
        actions={
          <>
            {card.canEdit && card.status === 'draft' ? (
              <Button
                className="min-h-[44px] md:min-h-0"
                variant="secondary"
                onClick={() => status.mutate('review')}
                loading={status.isPending}
              >
                На проверку
              </Button>
            ) : null}
            {card.canEdit && card.status === 'review' ? (
              <Button
                className="min-h-[44px] md:min-h-0"
                variant="ghost"
                onClick={() => status.mutate('draft')}
                loading={status.isPending}
              >
                Вернуть в черновик
              </Button>
            ) : null}
            {card.canApprove && card.status === 'review' ? (
              <Button
                className="min-h-[44px] md:min-h-0"
                variant="primary"
                onClick={() => status.mutate('approved')}
                loading={status.isPending}
              >
                Утвердить
              </Button>
            ) : null}
            {card.canEdit && card.isCurrent && card.status !== 'archived' ? (
              <Button
                className="min-h-[44px] md:min-h-0"
                variant={card.status === 'approved' ? 'primary' : 'secondary'}
                icon={<FileUp className="size-4" />}
                onClick={() => setNewVersion(true)}
              >
                Новая версия из KENML
              </Button>
            ) : null}
            {card.canEdit &&
            money &&
            card.isCurrent &&
            card.status === 'approved' &&
            card.kind === 'local' ? (
              <Button
                className="min-h-[44px] md:min-h-0"
                variant="secondary"
                loading={copyVersion.isPending}
                onClick={() => copyVersion.mutate()}
              >
                Копия для правок
              </Button>
            ) : null}
            {card.canEdit &&
            money &&
            card.isCurrent &&
            card.kind === 'local' &&
            card.status !== 'archived' ? (
              <Button
                className="min-h-[44px] md:min-h-0"
                variant="secondary"
                onClick={() => setReprice(true)}
              >
                Пересчитать в новый уровень цен
              </Button>
            ) : null}
            {money ? (
              <>
                <a
                  href={`/api/v1/estimates/${card.id}/export?format=pdf`}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-line px-4 text-[14px] font-medium text-ink hover:bg-surface-2 md:min-h-10"
                >
                  <Download className="size-4" /> PDF
                </a>
                <a
                  href={`/api/v1/estimates/${card.id}/export?format=xlsx`}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-line px-4 text-[14px] font-medium text-ink hover:bg-surface-2 md:min-h-10"
                >
                  <Download className="size-4" /> Excel
                </a>
                {card.kind === 'local' ? (
                  <>
                    <a
                      href={`/api/v1/estimates/${card.id}/export?format=kenml`}
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-line px-4 text-[14px] font-medium text-ink hover:bg-surface-2 md:min-h-10"
                    >
                      <Download className="size-4" /> KENML
                    </a>
                    <Button
                      className="min-h-[44px] md:min-h-10"
                      variant="secondary"
                      onClick={() => setKenml2(true)}
                    >
                      KENML 2.01
                    </Button>
                  </>
                ) : null}
              </>
            ) : null}
            {card.canEdit && card.status === 'approved' ? (
              <Button
                className="min-h-[44px] md:min-h-0"
                variant="ghost"
                onClick={() => status.mutate('archived')}
                loading={status.isPending}
              >
                В архив
              </Button>
            ) : null}
          </>
        }
      />

      <div className="flex flex-col gap-6">
        <TitleBlock
          items={[
            {
              label: 'Всего по смете',
              value: money ? tengeShort(card.totals.amount) : '—',
              note: money ? tenge(card.totals.amount) : 'суммы скрыты: нужен уровень «Акты и счета»',
            },
            { label: 'Строительные', value: money ? tengeShort(card.totals.construction) : '—' },
            { label: 'Монтажные', value: money ? tengeShort(card.totals.installation) : '—' },
            { label: 'Оборудование', value: money ? tengeShort(card.totals.equipment) : '—' },
            {
              label: 'ФОТ',
              value: money ? tengeShort(card.totals.wages) : '—',
              note: 'зарплата рабочих и машинистов',
            },
            { label: 'Трудоёмкость', value: hours(card.totals.laborHours), note: 'чел.-ч' },
          ]}
        />

        {card.importReport.manual.length ? (
          <div className="rounded-md border border-line bg-surface-2 p-4 text-[13px] text-ink-2">
            <p className="font-medium text-ink">
              После загрузки объёмы правились вручную — смета отличается от файла:
            </p>
            <ul className="mt-1 list-disc pl-5">
              {card.importReport.manual.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {card.importReport.issues.length ? (
          <div className="rounded-md border border-warning/40 bg-warning-soft p-4 text-[13px] text-warning">
            <p className="font-medium">
              Пересчёт расходится с файлом {card.importReport.fileName ?? ''} — смета не утверждается, пока
              расхождения не разобраны (СМ-13)
            </p>
            <ul className="mt-1 list-disc pl-5">
              {card.importReport.issues.slice(0, 10).map((s) => (
                <li key={s}>{s}</li>
              ))}
              {card.importReport.issues.length > 10 ? (
                <li>… и ещё {card.importReport.issues.length - 10}</li>
              ) : null}
            </ul>
          </div>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-3">
          <Panel title="О смете" className="lg:col-span-2">
            <dl className="grid gap-x-6 gap-y-2 text-[13px] sm:grid-cols-2">
              {[
                ['Стройка', [card.constructionCode, card.constructionName].filter(Boolean).join(' — ')],
                ['Объект', [card.objectCode, card.objectName].filter(Boolean).join(' — ')],
                ['Чертежи (основание)', card.drawings],
                ['Глава ССР', card.chapter],
                ['Стадия', card.designStage],
                ['Методика', card.methodology],
                ['Нормативная база', card.normBase],
                ['Ценовая зона', card.priceZone],
                ['Уровень цен', card.priceLevel],
                ['Составил', card.compiledBy],
                [
                  'Источник',
                  card.importReport.fileName
                    ? `${card.source === 'kenml' ? 'KENML' : card.source}, ${card.importReport.fileName}${card.importReport.generator ? ` (${card.importReport.generator})` : ''}`
                    : card.source,
                ],
                ['Загружена', card.importReport.importedAt ? dateTime(card.importReport.importedAt) : null],
                [
                  'Округление',
                  card.importReport.rounding === 'half-even'
                    ? 'половина к чётному, как в АВС'
                    : 'половина вверх',
                ],
                [
                  'Позиции-аналоги',
                  card.importReport.analogItems
                    ? `${card.importReport.analogItems} — стоимость единицы из файла, ресурсов для пересчёта нет`
                    : null,
                ],
                [
                  'Утверждена',
                  card.approvedAt
                    ? `${date(card.approvedAt)}${card.approvedByName ? `, ${card.approvedByName}` : ''}`
                    : null,
                ],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <Fragment key={k as string}>
                    <dt className="text-ink-2">{k}</dt>
                    <dd className="text-ink [overflow-wrap:anywhere]">{v}</dd>
                  </Fragment>
                ))}
            </dl>
            {card.note ? (
              <p className="mt-3 text-[13px] text-ink-2 whitespace-pre-line">{card.note}</p>
            ) : null}
          </Panel>
          <Panel title="Связи">
            <div className="flex flex-col gap-4">
              <BudgetLinePicker card={card} onSaved={refresh} />
              {card.kind === 'object' ? <ChapterPicker card={card} onSaved={refresh} /> : null}
              {card.budgetLineName ? (
                <p className="text-[13px] text-ink-2">
                  Сейчас:{' '}
                  <Link
                    to="/projects/$projectId/$tab"
                    params={{ projectId: card.projectId, tab: 'budget' }}
                    search={{ line: card.budgetLineId ?? undefined }}
                    className="inline-flex min-h-[44px] items-center text-accent-text underline md:min-h-0"
                  >
                    {card.budgetLineName}
                  </Link>
                </p>
              ) : null}
              <div>
                <span className="block text-[13px] font-medium text-ink">Версии</span>
                <ul className="mt-1 flex flex-col gap-1 text-[13px]">
                  {card.versions.map((v) => (
                    <li key={v.id} className="flex min-h-[28px] items-center gap-2">
                      {v.id === card.id ? (
                        <span className="font-medium">v{v.versionNo}</span>
                      ) : (
                        <Link
                          to="/estimates/$estimateId"
                          params={{ estimateId: v.id }}
                          className="inline-flex min-h-[44px] items-center text-accent-text underline md:min-h-0"
                        >
                          v{v.versionNo}
                        </Link>
                      )}
                      <span className="text-ink-2">{ESTIMATE_VERSION_KINDS[v.versionKind]}</span>
                      <StatusBadge status={v.status} />
                      <span className="num ml-auto text-ink-2">{money ? tengeShort(v.amount) : ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {card.children.length ? (
                <div>
                  <span className="block text-[13px] font-medium text-ink">Входят сметы</span>
                  <ul className="mt-1 flex flex-col gap-1 text-[13px]">
                    {card.children.map((c) => (
                      <li key={c.id}>
                        <Link
                          to="/estimates/$estimateId"
                          params={{ estimateId: c.id }}
                          className="inline-flex min-h-[44px] items-center text-accent-text underline md:min-h-0"
                        >
                          {c.code} {c.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {project.data ? (
                <Link
                  to="/estimates"
                  search={{ projectId: card.projectId }}
                  className="inline-flex min-h-[44px] items-center text-[13px] text-accent-text underline md:min-h-0"
                >
                  Все сметы проекта {project.data.code}
                </Link>
              ) : null}
            </div>
          </Panel>
          <ChangeImpact estimateId={card.id} projectId={card.projectId} />
        </div>

        <div className="flex flex-wrap items-center gap-2" role="tablist">
          {(
            [
              ...(card.kind === 'object'
                ? ([['form', 'Форма 3']] as const)
                : card.kind === 'summary'
                  ? ([['form', 'Сводный расчёт']] as const)
                  : []),
              ['items', `Позиции (${card.itemsCount})`],
              ['resources', 'Ресурсная ведомость'],
              ['compare', 'Сравнение версий'],
              ['check', 'Проверка'],
              ['offers', 'Сравнение с КП'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={view === k}
              onClick={() => setView(k)}
              className={clsx(
                'min-h-[44px] rounded-md px-4 text-[14px] font-medium',
                view === k ? 'bg-ink text-on-ink' : 'bg-surface text-ink-2 hover:text-ink',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {view === 'items' ? (
          <Panel
            title="Позиции сметы"
            meta={card.sections.length > 1 ? `${card.sections.length} разделов` : undefined}
            bodyClassName="p-0"
            actions={
              <>
                {editable ? (
                  <Button
                    className="min-h-[44px] md:min-h-0"
                    variant="primary"
                    icon={<Plus className="size-4" />}
                    onClick={() => setAdding({ after: null })}
                  >
                    Новая позиция
                  </Button>
                ) : null}
                {card.canEdit && card.sections.length === 1 ? (
                  <Button
                    className="min-h-[44px] md:min-h-0"
                    variant="ghost"
                    icon={
                      card.sections[0]!.lockedBy ? <Unlock className="size-4" /> : <Lock className="size-4" />
                    }
                    loading={lock.isPending}
                    onClick={() =>
                      lock.mutate({ sectionId: card.sections[0]!.id, lock: !card.sections[0]!.lockedBy })
                    }
                  >
                    {card.sections[0]!.lockedBy
                      ? `Снять блокировку (${card.sections[0]!.lockedByName ?? ''})`
                      : 'Заблокировать раздел за собой'}
                  </Button>
                ) : null}
              </>
            }
          >
            {card.itemsCount ? (
              <ItemsTable
                card={card}
                money={money}
                editable={editable}
                canLinkSchedule={canLinkSchedule}
                onChanged={refresh}
                onAddAfter={(id) => setAdding({ after: id })}
                onDelete={(it) => {
                  if (window.confirm(`Удалить позицию ${it.number} «${it.name}»?`)) del.mutate(it.id);
                }}
                onLock={(sec) => lock.mutate({ sectionId: sec.id, lock: !sec.lockedBy })}
                locking={lock.isPending}
              />
            ) : (
              <div className="p-5">
                <EmptyState
                  title="Позиций нет"
                  action={
                    editable ? (
                      <Button
                        className="min-h-[44px] md:min-h-0"
                        variant="primary"
                        icon={<Plus className="size-4" />}
                        onClick={() => setAdding({ after: null })}
                      >
                        Новая позиция
                      </Button>
                    ) : null
                  }
                >
                  Смета создана вручную: позиции появятся после загрузки KENML или добавления из нормативной
                  базы.
                </EmptyState>
              </div>
            )}
          </Panel>
        ) : null}
        {view === 'resources' ? (
          statement.isPending ? (
            <Spinner />
          ) : statement.isError ? (
            <ErrorBox error={statement.error} />
          ) : (
            <StatementTable
              st={statement.data}
              money={money}
              exportHref={`/api/v1/estimates/${card.id}/resources/export`}
            />
          )
        ) : null}
        {view === 'check' ? (
          check.isPending ? (
            <Spinner label="Проверка" />
          ) : check.isError ? (
            <ErrorBox error={check.error} />
          ) : check.data.length ? (
            <Panel
              title="Проверка сметы"
              meta="СМ-17: арифметика, дубли, коэффициенты, цены, объёмы"
              bodyClassName="p-0"
            >
              <ul className="divide-y divide-line">
                {check.data.map((f) => (
                  <li
                    key={`${f.code}|${f.itemId ?? ''}|${f.text}`}
                    className="flex flex-col gap-1 px-5 py-3 text-[13px] md:flex-row md:items-center md:gap-4"
                  >
                    <Badge
                      tone={f.severity === 'error' ? 'danger' : f.severity === 'warning' ? 'warning' : 'info'}
                    >
                      {f.severity === 'error'
                        ? 'ошибка'
                        : f.severity === 'warning'
                          ? 'проверить'
                          : 'к сведению'}
                    </Badge>
                    <span className="num w-12 shrink-0 text-ink-2">
                      {f.itemNumber !== null ? `поз. ${f.itemNumber}` : ''}
                    </span>
                    <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{f.text}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : (
            <EmptyState title="Замечаний нет">
              Арифметика сходится, дублей и пропущенных коэффициентов нет, цены ресурсов совпадают с
              действующими выпусками.
            </EmptyState>
          )
        ) : null}
        {view === 'form' && money ? (
          card.kind === 'object' ? (
            <ObjectFormTab estimateId={card.id} canEdit={card.canEdit && card.status !== 'approved'} />
          ) : (
            <SsrTab estimateId={card.id} canEdit={card.canEdit && card.status !== 'approved'} />
          )
        ) : view === 'form' ? (
          <EmptyState title="Суммы скрыты">Форма с суммами видна с уровнем денег «Акты и счета».</EmptyState>
        ) : null}
        {view === 'offers' ? (
          <Panel
            title="Сравнение КП со сметой"
            meta="СМ-18, БМ-05: цены КП без НДС против сметных, материалы и работы раздельно"
          >
            <OfferCompareTab projectId={card.projectId} estimateId={card.id} />
          </Panel>
        ) : null}
        {view === 'compare' ? (
          otherVersions.length ? (
            <div className="flex flex-col gap-4">
              <Field label="С какой версией сравнить" className="max-w-md" htmlFor="estimate-compare-with">
                <Select
                  id="estimate-compare-with"
                  className="min-h-11"
                  value={compareWith}
                  onChange={(e) => setCompareWith(e.target.value)}
                >
                  <option value="">— выберите версию —</option>
                  {otherVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.versionNo} · {ESTIMATE_VERSION_KINDS[v.versionKind]} · {date(v.createdAt)}
                    </option>
                  ))}
                </Select>
              </Field>
              {compareWith ? <DiffPanel card={card} otherId={compareWith} money={money} /> : null}
            </div>
          ) : (
            <EmptyState title="Других версий нет">
              Загрузите изменённую смету кнопкой «Новая версия из KENML» — портал сравнит версии построчно.
            </EmptyState>
          )
        ) : null}
      </div>

      {kenml2 ? <Kenml2Dialog card={card} onClose={() => setKenml2(false)} /> : null}
      {reprice ? (
        <RepriceDialog
          card={card}
          onClose={() => setReprice(false)}
          onDone={(r) => {
            setReprice(false);
            setRepriced(r);
            refresh();
          }}
        />
      ) : null}
      {repriced ? (
        <RepriceReportDialog
          r={repriced}
          onClose={() => {
            const id = repriced.estimateId;
            setRepriced(null);
            navigate({ to: '/estimates/$estimateId', params: { estimateId: id } });
          }}
        />
      ) : null}
      {adding ? (
        <AddItemDialog
          card={card}
          afterItemId={adding.after}
          onClose={() => setAdding(null)}
          onSaved={() => {
            setAdding(null);
            refresh();
          }}
        />
      ) : null}
      {newVersion && project.data ? (
        <ImportDialog
          project={project.data}
          rootId={card.rootId ?? card.id}
          rootCode={card.code}
          onClose={() => setNewVersion(false)}
          onDone={(r) => {
            setNewVersion(false);
            setDone(r);
            refresh();
          }}
        />
      ) : null}
      {done ? (
        <ImportResultDialog
          r={done}
          onClose={() => {
            const id = done.estimateId;
            setDone(null);
            navigate({ to: '/estimates/$estimateId', params: { estimateId: id } });
          }}
        />
      ) : null}
    </Page>
  );
}
